<script setup lang="ts">
import { MoreVerticalIcon } from '@blpde/nx-icons';
import { NxTooltip } from '../Tooltip';
import type { InlineIconProps } from './internal';

withDefaults(
  defineProps<{
    inline: InlineIconProps[];
    overflow: InlineIconProps[];
    inputDisabled: boolean;
    reveals: (icon: { revealOnInteraction?: boolean }) => boolean;
    showTooltips?: boolean;
    /**
     * The field sits in a form, not an NxTable cell: Tab goes to the next
     * field, so action buttons leave the Tab order unless an icon says
     * otherwise (`skipTab`). Cells keep them as Tab stops.
     */
    formField?: boolean;
  }>(),
  { showTooltips: true, formField: false }
);

const emit = defineEmits<{
  focusInput: [];
}>();
</script>

<template>
  <!-- Render element is decided by `onClick` alone: a handler → <button>
       (interactive, focusable); none → <span> (inert, click passes through
       to the field). The `deco` flag only affects classification (this
       action group + reveal + divider), never the rendered tag. -->
  <!-- @mousedown.prevent: keep focus in the field so blur/table handlers don't
       unmount or reset the control before the click lands. Tooltip wraps when
       `icon.tooltip` is set so hover explains the action. -->
  <template v-for="(icon, i) in inline" :key="`action-${i}`">
    <NxTooltip v-if="showTooltips && icon.tooltip" :text="icon.tooltip" side="top" :delay-ms="280">
      <component
        :is="icon.onClick ? 'button' : 'span'"
        class="inline-icon action"
        :class="{ reveal: reveals(icon) }"
        :type="icon.onClick ? 'button' : undefined"
        :disabled="icon.onClick ? inputDisabled : undefined"
        :aria-label="icon.onClick ? icon.label : undefined"
        :tabindex="icon.onClick && (icon.skipTab ?? formField) ? -1 : undefined"
        @mousedown="icon.onClick ? ($event.preventDefault(), $event.stopPropagation()) : undefined"
        @click="icon.onClick"
        @auxclick="icon.onClick ? icon.onAuxClick?.($event) : undefined"
        @keydown.enter.space.stop="() => {}"
      >
        <component
          :is="icon.component"
          :filled="icon.filled ?? false"
          :size="icon.size ?? '1.125em'"
          :color="icon.color ?? 'var(--nxt-brand-bg)'"
        />
      </component>
    </NxTooltip>
    <component
      :is="icon.onClick ? 'button' : 'span'"
      v-else
      class="inline-icon action"
      :class="{ reveal: reveals(icon) }"
      :type="icon.onClick ? 'button' : undefined"
      :disabled="icon.onClick ? inputDisabled : undefined"
      :aria-label="icon.onClick ? icon.label : undefined"
      :tabindex="icon.onClick && (icon.skipTab ?? formField) ? -1 : undefined"
      @mousedown="icon.onClick ? ($event.preventDefault(), $event.stopPropagation()) : undefined"
      @click="icon.onClick"
      @auxclick="icon.onClick ? icon.onAuxClick?.($event) : undefined"
      @keydown.enter.space.stop="() => {}"
    >
      <component
        :is="icon.component"
        :filled="icon.filled ?? false"
        :size="icon.size ?? '1.125em'"
        :color="icon.color ?? 'var(--nxt-brand-bg)'"
      />
    </component>
  </template>

  <template v-for="(icon, i) in overflow" :key="`surplus-${i}`">
    <NxTooltip v-if="showTooltips && icon.tooltip" :text="icon.tooltip" side="top" :delay-ms="280">
      <button
        type="button"
        class="inline-icon action surplus"
        :disabled="inputDisabled"
        :aria-label="icon.label"
        :tabindex="(icon.skipTab ?? formField) ? -1 : undefined"
        @mousedown.prevent.stop
        @click="icon.onClick"
        @auxclick="icon.onClick ? icon.onAuxClick?.($event) : undefined"
        @keydown.enter.space.stop="() => {}"
      >
        <component
          :is="icon.component"
          :filled="icon.filled ?? false"
          :size="icon.size ?? '1.125em'"
          :color="icon.color ?? 'var(--nxt-brand-bg)'"
        />
      </button>
    </NxTooltip>
    <button
      v-else
      type="button"
      class="inline-icon action surplus"
      :disabled="inputDisabled"
      :aria-label="icon.label"
      :tabindex="(icon.skipTab ?? formField) ? -1 : undefined"
      @mousedown.prevent.stop
      @click="icon.onClick"
      @auxclick="icon.onClick ? icon.onAuxClick?.($event) : undefined"
      @keydown.enter.space.stop="() => {}"
    >
      <component
        :is="icon.component"
        :filled="icon.filled ?? false"
        :size="icon.size ?? '1.125em'"
        :color="icon.color ?? 'var(--nxt-brand-bg)'"
      />
    </button>
  </template>

  <button
    v-if="overflow.length > 0"
    type="button"
    class="inline-icon action overflow-trigger"
    :disabled="inputDisabled"
    tabindex="-1"
    aria-hidden="true"
    @mousedown.prevent
    @click="emit('focusInput')"
  >
    <MoreVerticalIcon filled class="nxt-icon-md" color="var(--nxt-brand-bg)" />
  </button>
</template>

<style scoped>
.inline-icon {
  display: flex;
  border-radius: var(--nxt-radius-sm);
}

button.inline-icon {
  border: none;
  font-size: var(--nxt-font-size-md);
  outline: none;
  padding: 0;
  background: none;
  cursor: pointer;
}

button.inline-icon:enabled {
  transition: background-color var(--nxt-motion-fast);
}

button.inline-icon:enabled:hover {
  background-color: var(--nxt-surface-sunken-bg);
}

button.inline-icon:enabled:focus {
  outline: var(--nxt-hairline) solid var(--nxt-focus-ring);
}

button.inline-icon:disabled {
  pointer-events: none;
}
</style>
